import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import DOMPurify from 'dompurify';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  toast,
  VideoPlayer,
} from '@mlm/ui';
import {
  CONTENT_CHANGE_LABELS,
  CREATOR_SUBSCRIPTION_PERIOD_LABELS as PERIOD_LABELS,
  detectPlaybackProvider,
  diffWords,
} from '@mlm/shared';
import { apiErrorMessage, getTenantFeatures, startImpersonation } from '../lib/api';
import { hasRole } from '../lib/auth-store';
import { ReviewDecisionDialog } from '../components/review-decision-dialog';
import {
  adjustCreatorLedger,
  approveCreator,
  createCreator,
  createCreatorPayoutBatch,
  creatorRequirementFileUrl,
  exportCreatorPayoutBatch,
  getCourseDetail,
  getCreator,
  listAllCourses,
  listAllQuestions,
  listContentPendingCourses,
  listCourseContentChanges,
  listPendingCreatorPlans,
  reviewCreatorPlan,
  openLessonForReview,
  openPromoForReview,
  reviewCourseContent,
  listCreatorPayoutBatches,
  listCreatorPayouts,
  listCreators,
  markCreatorPayoutBatchPaid,
  markCreatorPayoutPaid,
  reactivateCreator,
  rejectCreator,
  rejectCreatorPayout,
  rejectCreatorPayoutBatch,
  reviewCreatorCourse,
  reviewCreatorRequirement,
  setQuestionHidden,
  suspendCreator,
  unpublishCourse,
  updateCreatorCommission,
  getTestAttemptDetail,
  gradeTestAttempt,
  listPendingTestAttempts,
  type AdminLessonPreview,
  type CreatorStats,
  type PendingCreatorPlanRow,
  type ContentChangeSnapshot,
  type CourseContentChangeRow,
} from '../lib/creators-api';

const statusVariant: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  approved: 'default',
  pending: 'secondary',
  requested: 'secondary',
  processing: 'secondary',
  rejected: 'destructive',
  suspended: 'destructive',
  paid: 'default',
  cancelled: 'outline',
  published: 'default',
  draft: 'outline',
};

const TAB_VALUES: string[] = ['accounts', 'courses', 'questions', 'payouts'];

/**
 * Admin → Creators (LMS). Three tabs mirroring the Merchants page: Accounts
 * (approve / reject / suspend, fee %, requirement review, ledger adjust),
 * Courses (every course — creator listing approval + unpublish, curriculum
 * peek, Q&A moderation), Payouts (single + batches).
 */
export function CreatorsPage() {
  // `?tab=` so an owner notification can deep-link to the right tab — a
  // payout alert that always landed on Accounts would be worse than no link.
  const [params, setParams] = useSearchParams();
  const raw = params.get('tab');
  const tab = TAB_VALUES.includes(raw ?? '') ? raw! : 'accounts';
  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold">Creators</h1>
      <Tabs
        value={tab}
        onValueChange={(v) => setParams(v === 'accounts' ? {} : { tab: v }, { replace: true })}
      >
        <TabsList>
          <TabsTrigger value="accounts">Accounts</TabsTrigger>
          <TabsTrigger value="courses">Courses</TabsTrigger>
          <TabsTrigger value="questions">Q&amp;A</TabsTrigger>
          <TabsTrigger value="payouts">Payouts</TabsTrigger>
        </TabsList>
        <TabsContent value="accounts">
          <AccountsTab />
        </TabsContent>
        <TabsContent value="courses">
          <CoursesTab />
        </TabsContent>
        <TabsContent value="questions">
          <QuestionsTab />
        </TabsContent>
        <TabsContent value="payouts">
          <PayoutsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ── Accounts ────────────────────────────────────────────────────────────────

function AccountsTab() {
  const [status, setStatus] = useState('pending');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [reviewing, setReviewing] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [impersonateError, setImpersonateError] = useState<string | null>(null);
  // Same gate as the Members page: only super_admin ever sees the option, and
  // the backend enforces it independently via requireRole('super_admin').
  const canImpersonate = hasRole('super_admin');
  const { data } = useQuery({
    queryKey: ['admin-creators', status, search, page],
    queryFn: () => listCreators({ status, search: search || undefined, page, pageSize }),
  });

  /**
   * Sign in as a creator, in the CREATOR portal. Worth having beyond ordinary
   * support: the house creator account is a SHARED login several staff use, so
   * going through here is what makes an audit row name the human who acted
   * instead of the shared account (`impersonator_user_id`).
   */
  async function handleImpersonate(userId: string) {
    setImpersonateError(null);
    try {
      const { url } = await startImpersonation(userId, 'creator');
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      const data = (err as { response?: { data?: { error?: string; message?: string } } }).response
        ?.data;
      setImpersonateError(
        data?.error === 'cannot_impersonate_super_admin'
          ? 'Cannot impersonate a super-admin.'
          : data?.error === 'target_not_active'
            ? 'That creator’s user account is not active.'
            : data?.error === 'target_not_creator'
              ? 'That user no longer holds the creator role.'
              : (data?.message ?? data?.error ?? 'Could not start impersonation.'),
      );
    }
  }
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <StatusFilter
          value={status}
          onChange={(v) => {
            setStatus(v);
            setPage(1);
          }}
          options={['pending', 'approved', 'rejected', 'suspended']}
        />
        <Input
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          placeholder="Search name or email…"
          className="ml-auto h-8 w-56"
        />
        <Button size="sm" onClick={() => setCreating(true)}>
          Create creator
        </Button>
      </div>
      {impersonateError && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {impersonateError}
        </div>
      )}
      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="border-b text-left text-muted-foreground">
              <tr>
                <th className="p-3">Creator</th>
                <th className="p-3">Owner</th>
                <th className="p-3">Courses</th>
                <th className="p-3">Students</th>
                <th className="p-3">Fee</th>
                <th className="p-3">Status</th>
                <th className="p-3" />
              </tr>
            </thead>
            <tbody>
              {(data?.rows ?? []).map((c) => (
                <tr key={c.userId} className="border-b last:border-0">
                  <td className="p-3 font-medium">
                    <span className="flex items-center gap-1">
                      {c.displayName}
                      {c.isHouse && (
                        <Badge variant="outline" className="text-[10px]">
                          House
                        </Badge>
                      )}
                    </span>
                  </td>
                  <td className="p-3">
                    {c.ownerName || c.ownerEmail}
                    <div className="text-xs text-muted-foreground">{c.ownerEmail}</div>
                  </td>
                  <td className="p-3 tabular-nums">{c.courseCount}</td>
                  <td className="p-3 tabular-nums">{c.studentCount}</td>
                  <td className="p-3 tabular-nums">
                    {c.platformFeePct != null ? `${Number(c.platformFeePct)}%` : '—'}
                  </td>
                  <td className="p-3">
                    <Badge variant={statusVariant[c.status]} className="capitalize">
                      {c.status}
                    </Badge>
                  </td>
                  <td className="p-3 text-right">
                    <div className="flex justify-end gap-2">
                      {canImpersonate && c.status === 'approved' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleImpersonate(c.userId)}
                        >
                          Login as
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => setReviewing(c.userId)}>
                        Review
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {data && data.rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-muted-foreground">
                    None.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
      {(data?.total ?? 0) > pageSize && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">
            Page {page} · {data?.total ?? 0} creators
          </span>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Previous
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={page * pageSize >= (data?.total ?? 0)}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
      {reviewing && <CreatorReviewDialog userId={reviewing} onClose={() => setReviewing(null)} />}
      {creating && (
        <CreateCreatorDialog
          onClose={() => setCreating(false)}
          onCreated={(userId, createdStatus) => {
            // A HOUSE creator is provisioned APPROVED — landing on the Pending
            // filter would show an empty list and look like the create failed.
            setStatus(createdStatus);
            setCreating(false);
            setReviewing(userId);
          }}
        />
      )}
    </div>
  );
}

function CreateCreatorDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (userId: string, status: string) => void;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState<Record<string, string>>({});
  const [isHouse, setIsHouse] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));
  const create = useMutation({
    mutationFn: () =>
      createCreator({
        email: form.email!,
        password: form.password!,
        firstName: form.firstName!,
        lastName: form.lastName!,
        phone: form.phone!,
        displayName: form.displayName!,
        platformFeePct: isHouse ? undefined : form.platformFeePct || undefined,
        isHouse: isHouse || undefined,
      }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['admin-creators'] });
      onCreated(r.userId, r.status);
    },
    onError: (e) => setError(apiErrorMessage(e)),
  });
  const valid =
    form.email &&
    (form.password?.length ?? 0) >= 8 &&
    form.firstName &&
    form.lastName &&
    form.phone &&
    form.displayName;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create creator</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Labeled label="First name">
            <Input value={form.firstName ?? ''} onChange={set('firstName')} />
          </Labeled>
          <Labeled label="Last name">
            <Input value={form.lastName ?? ''} onChange={set('lastName')} />
          </Labeled>
          <Labeled label="Email">
            <Input type="email" value={form.email ?? ''} onChange={set('email')} />
          </Labeled>
          <Labeled label="Password (≥ 8)">
            <Input type="password" value={form.password ?? ''} onChange={set('password')} />
          </Labeled>
          <Labeled label="Phone">
            <Input value={form.phone ?? ''} onChange={set('phone')} />
          </Labeled>
          <Labeled label="Display name">
            <Input value={form.displayName ?? ''} onChange={set('displayName')} />
          </Labeled>
          {!isHouse && (
            <Labeled label="Platform fee % (optional)">
              <Input
                type="number"
                min={0}
                max={100}
                value={form.platformFeePct ?? ''}
                onChange={set('platformFeePct')}
              />
            </Labeled>
          )}
        </div>
        <label className="flex items-start gap-2 text-sm">
          <Checkbox checked={isHouse} onCheckedChange={(v) => setIsHouse(v === true)} />
          <span>
            <b>House account</b> (our own staff)
            <span className="block text-xs text-muted-foreground">
              Courses are sold by the store: no platform fee, no creator wallet, and listings skip
              admin review.
            </span>
          </span>
        </label>
        <p className="text-xs text-muted-foreground">
          {isHouse ? (
            <>
              The account starts <b>approved</b> and can author right away.
            </>
          ) : (
            <>
              The creator starts <b>pending</b> — review documents, then approve.
            </>
          )}
        </p>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!valid || create.isPending} onClick={() => create.mutate()}>
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Live per-creator statistics inside the review dialog — the same numbers the
 * PUBLIC instructor page shows, from the same service, so an admin answering
 * "how is this instructor doing?" is not reading a different set of facts than
 * the learner browsing them.
 *
 * Subscribers is ABSENT (not zero) under the tenant-wide subscription model:
 * per-creator subscription does not exist there, and a hard zero would read as
 * "nobody subscribes to them".
 */
function CreatorStatsBlock({ stats }: { stats: CreatorStats }) {
  const tiles: { label: string; value: string }[] = [
    { label: 'Courses', value: String(stats.courseCount) },
    {
      label: 'Videos',
      value: `${stats.videoCount}${stats.r2VideoCount ? ` (${stats.r2VideoCount} hosted)` : ''}`,
    },
    { label: 'Students', value: String(stats.studentCount) },
    ...(stats.subscriberCount === null
      ? []
      : [{ label: 'Subscribers', value: String(stats.subscriberCount) }]),
    { label: 'Followers', value: String(stats.followers) },
    {
      label: 'Rating',
      value:
        stats.rating.count > 0 ? `${stats.rating.average.toFixed(1)} (${stats.rating.count})` : '—',
    },
    { label: 'Sales', value: String(stats.transactions) },
    { label: 'Joined', value: new Date(stats.joinedAt).toLocaleDateString() },
  ];
  return (
    <div className="rounded border p-3">
      <div className="text-xs font-medium uppercase text-muted-foreground">Statistics</div>
      <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {tiles.map((t) => (
          <div key={t.label}>
            <div className="text-[11px] text-muted-foreground">{t.label}</div>
            <div className="text-sm font-medium tabular-nums">{t.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CreatorReviewDialog({ userId, onClose }: { userId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const { data, refetch } = useQuery({
    queryKey: ['admin-creator', userId],
    queryFn: () => getCreator(userId),
  });
  const { data: features } = useQuery({
    queryKey: ['tenant-features'],
    queryFn: getTenantFeatures,
  });
  const [fee, setFee] = useState<string | null>(null);
  const [showFee, setShowFee] = useState<boolean | null>(null);
  const [house, setHouse] = useState<boolean | null>(null);
  const [reason, setReason] = useState('');
  const [adj, setAdj] = useState({ amount: '', direction: 'C' as 'C' | 'D', reason: '' });
  const [error, setError] = useState<string | null>(null);
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['admin-creators'] });
    refetch();
  };
  const run = (fn: () => Promise<unknown>) =>
    fn()
      .then(invalidate)
      .catch((e) => setError(apiErrorMessage(e)));

  if (!data) return null;
  const c = data.creator;
  const feeValue = fee ?? c.platformFeePct ?? features?.creator_platform_fee_pct ?? '30';
  const showFeeValue = showFee ?? c.showFeeToCreator;
  const houseValue = house ?? c.isHouse;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {c.displayName}
            <Badge variant={statusVariant[c.status]} className="capitalize">
              {c.status}
            </Badge>
          </DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 text-sm md:grid-cols-2">
          <div className="space-y-2">
            <div>
              <span className="text-muted-foreground">Owner: </span>
              {c.ownerName || '—'} · {c.ownerEmail}
            </div>
            {c.bio && <p className="whitespace-pre-line text-muted-foreground">{c.bio}</p>}
            {c.links.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {c.links.map((l) => (
                  <a
                    key={l.url}
                    href={l.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline"
                  >
                    {l.label}
                  </a>
                ))}
              </div>
            )}
            {c.rejectionReason && <p className="text-destructive">Reason: {c.rejectionReason}</p>}
            {data.stats && <CreatorStatsBlock stats={data.stats} />}
            <div className="rounded border p-3">
              <div className="text-xs font-medium uppercase text-muted-foreground">Wallet</div>
              <div className="mt-1 flex gap-4 tabular-nums">
                <span>Available ₱{Number(data.wallet.available).toFixed(2)}</span>
                <span>Lifetime ₱{Number(data.wallet.lifetimeEarned).toFixed(2)}</span>
              </div>
              <div className="mt-2 flex flex-wrap items-end gap-2">
                <Input
                  className="h-8 w-24"
                  placeholder="Amount"
                  value={adj.amount}
                  onChange={(e) => setAdj({ ...adj, amount: e.target.value })}
                />
                <select
                  className="h-8 rounded border bg-background px-2 text-sm"
                  value={adj.direction}
                  onChange={(e) => setAdj({ ...adj, direction: e.target.value as 'C' | 'D' })}
                >
                  <option value="C">Credit</option>
                  <option value="D">Debit</option>
                </select>
                <Input
                  className="h-8 flex-1"
                  placeholder="Reason"
                  value={adj.reason}
                  onChange={(e) => setAdj({ ...adj, reason: e.target.value })}
                />
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!adj.amount || !adj.reason}
                  onClick={() =>
                    run(() => adjustCreatorLedger(userId, adj)).then(() =>
                      setAdj({ amount: '', direction: 'C', reason: '' }),
                    )
                  }
                >
                  Adjust
                </Button>
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <div className="text-xs font-medium uppercase text-muted-foreground">Requirements</div>
            {data.submissions.length === 0 ? (
              <p className="text-muted-foreground">None submitted.</p>
            ) : (
              data.submissions.map((s) => (
                <div key={s.id} className="rounded border p-2">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{s.requirementKey}</span>
                    <Badge variant={statusVariant[s.status]} className="capitalize">
                      {s.status}
                    </Badge>
                  </div>
                  {s.textValue && <div className="text-muted-foreground">{s.textValue}</div>}
                  {s.fileKey && (
                    <button
                      className="text-xs underline"
                      onClick={() =>
                        creatorRequirementFileUrl(s.id).then((r) => window.open(r.url, '_blank'))
                      }
                    >
                      Open file
                    </button>
                  )}
                  {s.status === 'pending' && (
                    <div className="mt-1 flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => run(() => reviewCreatorRequirement(s.id, 'approved'))}
                      >
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() =>
                          run(() => reviewCreatorRequirement(s.id, 'rejected', reason || undefined))
                        }
                      >
                        Reject
                      </Button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 border-t pt-3">
          {!houseValue && (
            <>
              <Labeled label="Platform fee % (this creator)">
                <Input
                  type="number"
                  min={0}
                  max={100}
                  value={feeValue}
                  onChange={(e) => setFee(e.target.value)}
                />
              </Labeled>
              <label className="flex items-end gap-2 pb-2 text-sm">
                <input
                  type="checkbox"
                  checked={showFeeValue}
                  onChange={(e) => setShowFee(e.target.checked)}
                />
                Show the fee to the creator
              </label>
            </>
          )}
          {/* House is settable only on an APPROVED account — a pending
              application has no listings to bring across and the API 409s. */}
          {c.status === 'approved' && (
            <label className="col-span-2 flex items-start gap-2 text-sm">
              <Checkbox checked={houseValue} onCheckedChange={(v) => setHouse(v === true)} />
              <span>
                <b>House account</b> (our own staff)
                <span className="block text-xs text-muted-foreground">
                  Courses sell as store products: no platform fee, no creator wallet, listings skip
                  review. Save fee applies this.
                </span>
              </span>
            </label>
          )}
          <Labeled label="Reason (reject / suspend)">
            <Input value={reason} onChange={(e) => setReason(e.target.value)} />
          </Labeled>
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <DialogFooter className="flex-wrap gap-2">
          {c.status === 'approved' && (
            <Button
              variant="outline"
              onClick={() =>
                run(() =>
                  updateCreatorCommission(userId, {
                    ...(houseValue ? {} : { platformFeePct: feeValue }),
                    showFeeToCreator: showFeeValue,
                    isHouse: houseValue,
                  }),
                )
              }
            >
              {houseValue ? 'Save' : 'Save fee'}
            </Button>
          )}
          {c.status === 'pending' && (
            <>
              <Button
                variant="ghost"
                onClick={() => run(() => rejectCreator(userId, reason || undefined))}
              >
                Reject
              </Button>
              <Button
                onClick={() =>
                  run(() =>
                    approveCreator(userId, {
                      platformFeePct: feeValue,
                      showFeeToCreator: showFeeValue,
                    }),
                  )
                }
              >
                Approve at {Number(feeValue)}%
              </Button>
            </>
          )}
          {c.status === 'approved' && (
            <Button
              variant="destructive"
              onClick={() => run(() => suspendCreator(userId, reason || undefined))}
            >
              Suspend
            </Button>
          )}
          {(c.status === 'suspended' || c.status === 'rejected') && (
            <Button onClick={() => run(() => reactivateCreator(userId))}>Reactivate</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Courses ─────────────────────────────────────────────────────────────────

function CoursesTab() {
  const qc = useQueryClient();
  // Scope is resolved SERVER-side: 'house' means no creator OR a house creator
  // (the tenant's own staff now author through a creator account), which no
  // client-side predicate over one page could answer correctly.
  const [scope, setScope] = useState<'creator' | 'house' | 'all'>('creator');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [peek, setPeek] = useState<string | null>(null);
  const { data } = useQuery({
    queryKey: ['admin-all-courses', scope, search, page],
    queryFn: () => listAllCourses({ scope, q: search || undefined, page, pageSize }),
  });
  const shown = data?.rows ?? [];
  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin-all-courses'] });
  const unpublish = useMutation({
    mutationFn: (id: string) => unpublishCourse(id),
    onSuccess: invalidate,
  });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-1">
        {(['creator', 'house', 'all'] as const).map((o) => (
          <Button
            key={o}
            size="sm"
            variant={scope === o ? 'default' : 'outline'}
            className="capitalize"
            onClick={() => {
              setScope(o);
              setPage(1);
            }}
          >
            {o === 'creator' ? 'Creator courses' : o === 'house' ? 'House courses' : 'All'}
          </Button>
        ))}
        <Input
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          placeholder="Search course name…"
          className="ml-auto h-8 w-56"
        />
      </div>
      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="border-b text-left text-muted-foreground">
              <tr>
                <th className="p-3">Course</th>
                <th className="p-3">Creator</th>
                <th className="p-3">Topic</th>
                <th className="p-3">Access</th>
                <th className="p-3 text-right">Price</th>
                <th className="p-3 text-right">Lessons</th>
                <th className="p-3 text-right">Enrolled</th>
                <th className="p-3">Listing</th>
                <th className="p-3">Status</th>
                <th className="p-3" />
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => (
                <tr key={r.id} className="border-b last:border-0">
                  <td className="p-3 font-medium">
                    <button className="hover:underline" onClick={() => setPeek(r.id)}>
                      {r.productName}
                    </button>
                  </td>
                  <td className="p-3">
                    {r.creatorName ? (
                      <span className="flex items-center gap-1">
                        {r.creatorName}
                        {r.creatorIsHouse && (
                          <Badge variant="outline" className="text-[10px]">
                            House
                          </Badge>
                        )}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">House</span>
                    )}
                  </td>
                  <td className="p-3">
                    {r.categoryName ?? <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="p-3 capitalize">{r.accessLevel}</td>
                  <td className="p-3 text-right tabular-nums">₱{Number(r.price).toFixed(2)}</td>
                  <td className="p-3 text-right tabular-nums">{r.lessonCount}</td>
                  <td className="p-3 text-right tabular-nums">{r.enrollmentCount}</td>
                  <td className="p-3">
                    <Badge variant={statusVariant[r.approvalStatus]} className="capitalize">
                      {r.approvalStatus}
                    </Badge>
                  </td>
                  <td className="p-3">
                    <div className="flex flex-wrap items-center gap-1">
                      <Badge variant={statusVariant[r.status]} className="capitalize">
                        {r.status}
                      </Badge>
                      {/* Content review is a SECOND axis, so it rides beside the
                          publish state rather than replacing it: a published,
                          listing-approved course can still be selling content
                          nobody has looked at since it changed. */}
                      {r.contentReviewStatus === 'pending' && (
                        <Badge variant="warning">Content pending</Badge>
                      )}
                      {r.contentReviewStatus === 'rejected' && (
                        <Badge variant="muted">Content rejected</Badge>
                      )}
                    </div>
                  </td>
                  <td className="p-3 text-right">
                    <div className="flex justify-end gap-1">
                      {/* Phase 10: no bare Approve here — approving a listing
                          from a table row means approving something nobody
                          opened; the decision belongs in the peek, with the
                          outline, the lesson previews and the compare.
                          Round 2: no "Review" button either. It opened the
                          exact same dialog as the course name beside it and as
                          the queue card above, so one course offered three
                          identical doors. The queue card is the queue; the
                          course name is the door. */}
                      {r.status === 'published' && (
                        <Button size="sm" variant="outline" onClick={() => unpublish.mutate(r.id)}>
                          Unpublish
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {shown.length === 0 && (
                <tr>
                  <td colSpan={10} className="p-8 text-center text-muted-foreground">
                    None.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
      {(data?.total ?? 0) > pageSize && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">
            Page {page} · {data?.total ?? 0} courses
          </span>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Previous
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={page * pageSize >= (data?.total ?? 0)}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
      <ContentReviewCard onPeek={setPeek} />
      <PendingPlansCard />
      <PendingReviewsCard />
      {/* Both listing and content decisions now live INSIDE the peek — see the
          Review button above. */}
      {peek && <CoursePeekDialog id={peek} onClose={() => setPeek(null)} />}
    </div>
  );
}

/**
 * Phase 10 — third-party creators' SUBSCRIPTION plans awaiting approval.
 *
 * A separate card from the content queue because the question is different:
 * not "is what we sell still what we approved?" but "may this instructor
 * charge this, monthly?". House plans are born approved and never appear.
 */
function PendingPlansCard() {
  const qc = useQueryClient();
  const [reject, setReject] = useState<PendingCreatorPlanRow | null>(null);
  const { data: rows = [] } = useQuery({
    queryKey: ['admin-pending-creator-plans'],
    queryFn: listPendingCreatorPlans,
  });
  const review = useMutation({
    mutationFn: ({
      planId,
      decision,
      reason,
    }: {
      planId: string;
      decision: 'approved' | 'rejected';
      reason?: string;
    }) => reviewCreatorPlan(planId, decision, reason),
    meta: { successMessage: 'Subscription plan decision saved.' },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-pending-creator-plans'] });
      setReject(null);
    },
  });
  if (rows.length === 0) return null;
  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            Subscription plans awaiting review ({rows.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <ul className="divide-y">
            {rows.map((r) => (
              <li key={r.planId} className="flex flex-wrap items-center gap-2 p-4 text-sm">
                <span className="font-medium">{r.creatorName ?? 'Creator'}</span>
                <Badge variant="outline">{PERIOD_LABELS[r.period] ?? r.period}</Badge>
                <span className="tabular-nums">₱{Number(r.price).toFixed(2)}</span>
                <span className="text-xs text-muted-foreground">
                  submitted {new Date(r.submittedAt).toLocaleString()}
                </span>
                <div className="ml-auto flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => setReject(r)}>
                    Reject
                  </Button>
                  <Button
                    size="sm"
                    disabled={review.isPending}
                    onClick={() => review.mutate({ planId: r.planId, decision: 'approved' })}
                  >
                    Approve
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
      <ReviewDecisionDialog
        open={!!reject}
        onOpenChange={(o) => !o && setReject(null)}
        title="Reject this subscription plan"
        description={
          reject ? (
            <>
              <span className="font-medium text-foreground">{reject.creatorName ?? 'Creator'}</span>
              's {PERIOD_LABELS[reject.period] ?? reject.period} plan will not be sellable. Anyone
              already subscribed keeps the time they paid for.
            </>
          ) : undefined
        }
        label="Reason (optional)"
        placeholder="What the creator has to change before this can sell…"
        confirmLabel="Reject plan"
        pending={review.isPending}
        onConfirm={(note) =>
          review.mutate({ planId: reject!.planId, decision: 'rejected', reason: note || undefined })
        }
      />
    </>
  );
}

/**
 * Courses whose creator swapped playable content AFTER the listing was
 * approved. Separate queue from the listing decision above: the question here
 * is not "should we sell this?" but "is what we are already selling still what
 * we approved?" — and the answer needs the reviewer to WATCH something, which
 * is what the peek dialog is now for.
 *
 * Ordered oldest-change-first by the API: a course that has been serving
 * unreviewed video the longest is the one to open next.
 */
function ContentReviewCard({ onPeek }: { onPeek: (courseId: string) => void }) {
  const { data: rows = [] } = useQuery({
    queryKey: ['admin-content-pending'],
    queryFn: listContentPendingCourses,
  });
  if (rows.length === 0) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Changes awaiting review ({rows.length})</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <ul className="divide-y">
          {rows.map((r) => (
            <li key={r.courseId} className="flex flex-wrap items-center gap-2 p-4 text-sm">
              <span className="font-medium">{r.name}</span>
              <Badge variant="warning">
                {r.changeCount} change{r.changeCount === 1 ? '' : 's'}
              </Badge>
              <Badge variant={statusVariant[r.status]} className="capitalize">
                {r.status}
              </Badge>
              <span className="text-xs text-muted-foreground">
                {r.creatorName ?? 'House'}
                {r.contentChangedAt
                  ? ` · changed ${new Date(r.contentChangedAt).toLocaleString()}`
                  : ''}
              </span>
              <Button size="sm" className="ml-auto" onClick={() => onPeek(r.courseId)}>
                Review
              </Button>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

/**
 * Typed-answer test submissions on HOUSE courses awaiting a grade (creator
 * courses grade in the creator portal). Passing a submission mints the
 * learner's certificate in the same request.
 */
function PendingReviewsCard() {
  const qc = useQueryClient();
  const { data: rows = [] } = useQuery({
    queryKey: ['admin-test-attempts'],
    queryFn: listPendingTestAttempts,
  });
  const [openId, setOpenId] = useState<string | null>(null);
  if (rows.length === 0) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Pending test reviews (house courses)</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <ul className="divide-y">
          {rows.map((r) => (
            <li key={r.id} className="p-4">
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <span className="font-medium text-foreground">{r.memberName ?? 'Learner'}</span>
                <span>· {r.courseName}</span>
                <span>· {r.testTitle}</span>
                <span>· attempt #{r.attemptNumber}</span>
                <span>· {new Date(r.submittedAt).toLocaleString()}</span>
                <Badge variant="outline">
                  auto {r.autoScore}/{r.maxScore}
                </Badge>
              </div>
              {openId === r.id ? (
                <AdminGradeForm
                  attemptId={r.id}
                  onDone={() => {
                    setOpenId(null);
                    qc.invalidateQueries({ queryKey: ['admin-test-attempts'] });
                  }}
                />
              ) : (
                <Button className="mt-2" size="sm" onClick={() => setOpenId(r.id)}>
                  Review
                </Button>
              )}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

function AdminGradeForm({ attemptId, onDone }: { attemptId: string; onDone: () => void }) {
  const { data: attempt } = useQuery({
    queryKey: ['admin-test-attempt', attemptId],
    queryFn: () => getTestAttemptDetail(attemptId),
  });
  const [grades, setGrades] = useState<Record<string, { points: string; comment: string }>>({});
  const submit = useMutation({
    meta: { successMessage: 'Graded.' },
    mutationFn: () =>
      gradeTestAttempt(
        attemptId,
        attempt!.entries
          .filter((e) => e.kind === 'essay')
          .map((e) => ({
            questionId: e.questionId,
            points: Math.min(e.points, Math.max(0, Number(grades[e.questionId]?.points ?? 0))),
            comment: grades[e.questionId]?.comment?.trim() || null,
          })),
      ),
    onSuccess: (res) => {
      // A grade can be the last gate on a course, so this is the moment the
      // learner's certificate mints. Worth saying out loud.
      if (res.certificateIssued) toast.success('Certificate issued to the learner.');
      onDone();
    },
  });
  if (!attempt) return <p className="mt-2 text-sm text-muted-foreground">Loading…</p>;
  const essays = attempt.entries.filter((e) => e.kind === 'essay');
  const ready = essays.every((e) => grades[e.questionId]?.points !== undefined);
  return (
    <div className="mt-3 space-y-3">
      {attempt.entries.map((e, i) => (
        <div key={e.questionId} className="rounded border p-3 text-sm">
          <p className="font-medium">
            {i + 1}. {e.prompt}{' '}
            <span className="font-normal text-muted-foreground">({e.points} pts)</span>
          </p>
          {e.kind === 'multiple_choice' ? (
            <p className="mt-1 text-muted-foreground">
              Answered: {e.selectedIndex != null ? (e.choices[e.selectedIndex] ?? '—') : '—'} —{' '}
              {e.awardedPoints >= e.points ? 'correct' : 'wrong'}
            </p>
          ) : (
            <>
              <p className="mt-1 whitespace-pre-line rounded bg-muted/50 p-2">
                {e.text?.trim() || '— no answer —'}
              </p>
              <div className="mt-2 flex flex-wrap items-start gap-2">
                <Input
                  className="w-24"
                  inputMode="numeric"
                  placeholder={`0–${e.points}`}
                  value={grades[e.questionId]?.points ?? ''}
                  onChange={(ev) =>
                    setGrades((g) => ({
                      ...g,
                      [e.questionId]: {
                        points: ev.target.value.replace(/\D/g, ''),
                        comment: g[e.questionId]?.comment ?? '',
                      },
                    }))
                  }
                />
                <Textarea
                  className="flex-1"
                  rows={2}
                  placeholder="Feedback (optional)"
                  value={grades[e.questionId]?.comment ?? ''}
                  onChange={(ev) =>
                    setGrades((g) => ({
                      ...g,
                      [e.questionId]: {
                        points: g[e.questionId]?.points ?? '',
                        comment: ev.target.value,
                      },
                    }))
                  }
                />
              </div>
            </>
          )}
        </div>
      ))}
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onDone}>
          Cancel
        </Button>
        <Button size="sm" disabled={!ready || submit.isPending} onClick={() => submit.mutate()}>
          Submit grade
        </Button>
      </div>
    </div>
  );
}

/** Trim a URL to host + a stub of its path — enough to see youtube→vimeo. */
function shortUrl(url: string): string {
  try {
    const u = new URL(url);
    const tail = `${u.pathname}${u.search}`;
    return `${u.hostname.replace(/^www\./, '')}${tail.length > 22 ? `${tail.slice(0, 22)}…` : tail}`;
  } catch {
    return url.length > 36 ? `${url.slice(0, 36)}…` : url;
  }
}

/**
 * One side of a change, in as few words as carry meaning. The server already
 * reduced the payload (an R2 object key arrives as the literal 'set', an
 * article body as a length) so there is nothing here to redact — only to
 * shorten. A missing side is a create or a delete, not an error.
 */
function describeSnapshot(snap: ContentChangeSnapshot): string {
  if (!snap) return '—';
  if (snap.embedUrl) return shortUrl(snap.embedUrl);
  if (snap.sourceType === 'r2') return snap.videoKey === 'set' ? 'upload' : 'upload (no file)';
  if (snap.sourceType === 'article') {
    return snap.bodyLength != null ? `article (${snap.bodyLength} chars)` : 'article';
  }
  return snap.sourceType ?? '—';
}

/**
 * Phase 10 — the before/after compare.
 *
 * Field names as a REVIEWER would say them. `srp` and `accessLevel` are our
 * words, not theirs, and an approval queue that speaks in column names is one
 * more reason to click Approve without reading.
 */
const CHANGE_FIELD_LABELS: Record<string, string> = {
  name: 'Title',
  title: 'Title',
  subtitle: 'Subtitle',
  description: 'Description',
  price: 'Price',
  srp: 'Compare-at price',
  learnPoints: 'What you will learn',
  requirements: 'Requirements',
  level: 'Level',
  language: 'Language',
  accessLevel: 'Access',
  status: 'Publish state',
  topic: 'Topic',
  tags: 'Tags',
  freePreview: 'Free preview',
  dripDays: 'Unlocks after (days)',
  dripDate: 'Unlocks on',
  durationSeconds: 'Duration (seconds)',
  passingScorePct: 'Passing score (%)',
  maxAttempts: 'Max attempts',
  active: 'Active',
  practice: 'Practice test',
  order: 'Order',
  prompt: 'Question',
  options: 'Answer options',
};

/**
 * Identifiers that are in the payload so the row is traceable, not so a human
 * reads them. Rendering "testId: 9f3c… → 9f3c…" as a change would be noise at
 * best and a false positive at worst.
 */
const HIDDEN_CHANGE_FIELDS = new Set([
  'testId',
  'groupId',
  'linkedCourseId',
  'templateId',
  'kind',
  'html',
  'truncated',
  'sourceType',
  'embedUrl',
  'videoKey',
  'bodyLength',
]);

const MONEY_FIELDS = new Set(['price', 'srp']);

function peso(value: unknown): string {
  const n = Number(value);
  return Number.isFinite(n)
    ? `₱${n.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : '—';
}

/** Word-level diff, deletions struck through and insertions highlighted. */
function DiffText({ before, after }: { before: string; after: string }) {
  const segments = useMemo(() => diffWords(before, after), [before, after]);
  return (
    <p className="whitespace-pre-wrap break-words text-xs leading-relaxed">
      {segments.map((seg, i) =>
        seg.op === 'equal' ? (
          <span key={i}>{seg.text}</span>
        ) : seg.op === 'delete' ? (
          <del key={i} className="bg-destructive/10 text-destructive line-through decoration-1">
            {seg.text}
          </del>
        ) : (
          <ins
            key={i}
            className="bg-emerald-500/15 text-emerald-700 no-underline dark:text-emerald-400"
          >
            {seg.text}
          </ins>
        ),
      )}
    </p>
  );
}

function ChipList({ items, tone }: { items: string[]; tone: 'added' | 'removed' }) {
  if (!items.length) return null;
  return (
    <div className="flex flex-wrap gap-1">
      {items.map((item) => (
        <span
          key={item}
          className={
            tone === 'added'
              ? 'rounded bg-emerald-500/15 px-1.5 py-0.5 text-[11px] text-emerald-700 dark:text-emerald-400'
              : 'rounded bg-destructive/10 px-1.5 py-0.5 text-[11px] text-destructive line-through'
          }
        >
          {item}
        </span>
      ))}
    </div>
  );
}

function scalarText(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'on' : 'off';
  return String(value);
}

/** One field's before → after, presented by what the value IS. */
function FieldChange({ field, before, after }: { field: string; before: unknown; after: unknown }) {
  const label = CHANGE_FIELD_LABELS[field] ?? field;

  if (Array.isArray(before) || Array.isArray(after)) {
    const was = (Array.isArray(before) ? before : []).map(String);
    const is = (Array.isArray(after) ? after : []).map(String);
    // An ORDER change has the same members on both sides, so added/removed
    // chips would render nothing at all — show the sequences instead.
    if (field === 'order' || (was.length === is.length && was.every((v) => is.includes(v)))) {
      return (
        <div className="space-y-0.5">
          <div className="text-[11px] font-medium text-muted-foreground">{label}</div>
          <div className="text-xs text-muted-foreground line-through">{was.join(' · ') || '—'}</div>
          <div className="text-xs">{is.join(' · ') || '—'}</div>
        </div>
      );
    }
    return (
      <div className="space-y-1">
        <div className="text-[11px] font-medium text-muted-foreground">{label}</div>
        <ChipList items={was.filter((v) => !is.includes(v))} tone="removed" />
        <ChipList items={is.filter((v) => !was.includes(v))} tone="added" />
      </div>
    );
  }

  if (MONEY_FIELDS.has(field)) {
    return (
      <div className="text-xs">
        <span className="font-medium text-muted-foreground">{label}: </span>
        <span className="tabular-nums line-through">{peso(before)}</span>
        <span className="mx-1">→</span>
        <span className="font-medium tabular-nums">{peso(after)}</span>
      </div>
    );
  }

  const wasText = typeof before === 'string' ? before : '';
  const isText = typeof after === 'string' ? after : '';
  // Prose gets a word diff; a short label or an enum is clearer as a plain
  // "was → is" than as a diff of two words.
  if (wasText.length + isText.length > 80) {
    return (
      <div className="space-y-1">
        <div className="text-[11px] font-medium text-muted-foreground">{label}</div>
        <DiffText before={wasText} after={isText} />
      </div>
    );
  }
  return (
    <div className="text-xs">
      <span className="font-medium text-muted-foreground">{label}: </span>
      <span className="line-through">{scalarText(before)}</span>
      <span className="mx-1">→</span>
      <span className="font-medium">{scalarText(after)}</span>
    </div>
  );
}

/** Side-by-side sanitized article HTML, with a word-diff of the text behind a toggle. */
function ArticleBodyChange({ change }: { change: CourseContentChangeRow }) {
  const [asDiff, setAsDiff] = useState(false);
  const beforeHtml = typeof change.before?.html === 'string' ? change.before.html : '';
  const afterHtml = typeof change.after?.html === 'string' ? change.after.html : '';
  const truncated = change.before?.truncated === true || change.after?.truncated === true;
  // Sanitized AGAIN on render even though the API sanitized on write: this is
  // creator-authored HTML going into an admin's dangerouslySetInnerHTML, and
  // one sanitizer between a third party and a staff session is not enough.
  const clean = useMemo(
    () => ({
      before: DOMPurify.sanitize(beforeHtml, { ADD_ATTR: ['target'] }),
      after: DOMPurify.sanitize(afterHtml, { ADD_ATTR: ['target'] }),
      beforeText: DOMPurify.sanitize(beforeHtml, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] }),
      afterText: DOMPurify.sanitize(afterHtml, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] }),
    }),
    [beforeHtml, afterHtml],
  );
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <span className="text-[11px] font-medium text-muted-foreground">Article text</span>
        <Button
          size="sm"
          variant="ghost"
          className="h-6 px-2 text-[11px]"
          onClick={() => setAsDiff((v) => !v)}
        >
          {asDiff ? 'Side by side' : 'Show text diff'}
        </Button>
        {truncated && (
          <Badge variant="outline" className="text-[10px]">
            truncated
          </Badge>
        )}
      </div>
      {asDiff ? (
        <div className="max-h-56 overflow-auto rounded border bg-background p-2">
          <DiffText before={clean.beforeText} after={clean.afterText} />
        </div>
      ) : (
        <div className="grid gap-2 sm:grid-cols-2">
          <div className="max-h-56 overflow-auto rounded border bg-muted/30 p-2">
            <div className="mb-1 text-[10px] uppercase text-muted-foreground">Before</div>
            <article
              className="prose prose-sm max-w-none dark:prose-invert"
              dangerouslySetInnerHTML={{ __html: clean.before }}
            />
          </div>
          <div className="max-h-56 overflow-auto rounded border bg-background p-2">
            <div className="mb-1 text-[10px] uppercase text-muted-foreground">After</div>
            <article
              className="prose prose-sm max-w-none dark:prose-invert"
              dangerouslySetInnerHTML={{ __html: clean.after }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * The field-by-field body of an editorial change row. Returns null for the
 * Phase-8 SHAPE payloads, which `describeSnapshot` already renders in one line.
 */
function changeDetailKeys(change: CourseContentChangeRow): string[] {
  return Array.from(
    new Set([...Object.keys(change.before ?? {}), ...Object.keys(change.after ?? {})]),
  ).filter((k) => !HIDDEN_CHANGE_FIELDS.has(k));
}

function ChangeDetail({ change }: { change: CourseContentChangeRow }) {
  if (change.kind === 'lesson_body') return <ArticleBodyChange change={change} />;
  const keys = changeDetailKeys(change);
  if (!keys.length) return null;
  return (
    <div className="mt-1 space-y-1.5 border-l-2 pl-3">
      {keys.map((k) => (
        <FieldChange
          key={k}
          field={k}
          before={(change.before ?? {})[k]}
          after={(change.after ?? {})[k]}
        />
      ))}
    </div>
  );
}

function ContentChangeRow({ change }: { change: CourseContentChangeRow }) {
  const before = describeSnapshot(change.before);
  const after = describeSnapshot(change.after);
  // An editorial row carries VALUES, and `ChangeDetail` renders them field by
  // field; the one-line source summary is for the Phase-8 SHAPE payloads and
  // would read as "— → —" here.
  const detailed = change.kind === 'lesson_body' || changeDetailKeys(change).length > 0;
  return (
    <li className={change.resolvedAt ? 'py-1.5 opacity-60' : 'py-1.5'}>
      <div className="flex flex-wrap items-baseline gap-x-2">
        <span className="font-medium">{change.lessonTitle}</span>
        <span className="text-muted-foreground">
          {CONTENT_CHANGE_LABELS[change.kind] ?? change.kind}
        </span>
        {/* Tier 2 is recorded for context and does NOT gate the decision — say
            so, or a reviewer reads a caption upload as a reason to reject. */}
        {change.tier === 2 && (
          <Badge variant="outline" className="text-[10px]">
            context only
          </Badge>
        )}
        <span className="ml-auto text-xs text-muted-foreground">
          {new Date(change.createdAt).toLocaleString()}
        </span>
      </div>
      {!detailed && (before !== '—' || after !== '—') && (
        <div className="text-xs text-muted-foreground">
          {before} → {after}
        </div>
      )}
      {detailed && <ChangeDetail change={change} />}
      {change.resolvedAt && (
        <div className="text-xs text-muted-foreground">
          {change.resolution ?? 'resolved'} · {new Date(change.resolvedAt).toLocaleString()}
        </div>
      )}
    </li>
  );
}

/**
 * The reviewer's copy of one lesson. Goes through the shared `VideoPlayer`
 * rather than a bare element so an embed still gets the provider handshake
 * (a YouTube watch URL in a plain iframe renders YouTube's refusal page), and
 * deliberately carries NO `onEvent`: a reviewer watching is not a learner
 * opening, and a progress write from here would corrupt the opens funnel.
 *
 * Captions are passed empty on purpose — turning API caption paths into the
 * blob URLs the player wants is a member-side pipeline, and a reviewer judges
 * the picture, not the subtitles.
 */
function LessonPreviewPane({ preview }: { preview: AdminLessonPreview }) {
  const articleHtml = useMemo(() => {
    if (preview.kind !== 'article') return null;
    // Pre-Phase-4 bodies are plain text; rendering those as HTML would collapse
    // every line break into a single paragraph.
    if (!preview.body.includes('<')) return null;
    return DOMPurify.sanitize(preview.body, { ADD_ATTR: ['target'] });
  }, [preview]);

  if (preview.kind === 'article') {
    return (
      <div className="max-h-72 overflow-auto rounded border bg-background p-4">
        {articleHtml == null ? (
          <div className="whitespace-pre-wrap text-sm leading-relaxed">{preview.body}</div>
        ) : (
          <article
            className="prose prose-sm max-w-none dark:prose-invert"
            dangerouslySetInnerHTML={{ __html: articleHtml }}
          />
        )}
      </div>
    );
  }
  return (
    <div className="overflow-hidden rounded border">
      <VideoPlayer
        source={
          preview.kind === 'video'
            ? { kind: 'video', src: preview.src, captions: [] }
            : { kind: 'iframe', src: preview.src, provider: detectPlaybackProvider(preview.src) }
        }
        title={preview.title}
        protocolId={preview.lessonId}
        chrome={preview.kind === 'video' ? 'native' : 'embed-strip'}
        preferences={{ rate: 1, captionsLang: null }}
      />
    </div>
  );
}

/**
 * Curriculum peek, and — since Phase 8 — the content-review desk. It used to
 * list lesson TITLES and nothing else, which made re-review unactionable: an
 * approver could see that a lesson existed but never what it now plays.
 */
function CoursePeekDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['admin-course', id], queryFn: () => getCourseDetail(id) });
  const { data: changes = [] } = useQuery({
    queryKey: ['admin-course-content-changes', id],
    queryFn: () => listCourseContentChanges(id),
  });
  const [preview, setPreview] = useState<AdminLessonPreview | null>(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [listingRejectOpen, setListingRejectOpen] = useState(false);

  const openLesson = useMutation({
    mutationFn: (lessonId: string) => openLessonForReview(id, lessonId),
    onSuccess: setPreview,
  });

  const openPromo = useMutation({
    mutationFn: () => openPromoForReview(id),
    // The promo pane reuses the lesson player, so it borrows the lesson shape.
    onSuccess: (src) => setPreview({ ...src, lessonId: `promo-${id}`, title: 'Promo video' }),
  });

  const contentReview = useMutation({
    mutationFn: (args: { decision: 'approved' | 'rejected'; reason?: string }) =>
      reviewCourseContent(id, args.decision, args.reason),
    meta: { successMessage: 'Content decision saved.' },
    onSuccess: () => {
      // Four readers of this course's review state, and a stale one of them is
      // a reviewer ruling twice on the same change set.
      void qc.invalidateQueries({ queryKey: ['admin-content-pending'] });
      void qc.invalidateQueries({ queryKey: ['admin-all-courses'] });
      void qc.invalidateQueries({ queryKey: ['admin-course', id] });
      void qc.invalidateQueries({ queryKey: ['admin-course-content-changes', id] });
      onClose();
    },
  });

  /**
   * Phase 10 — the LISTING decision, made from the same desk.
   *
   * It used to live as bare Approve / Reject buttons on the courses table,
   * which is what made "approve without opening anything" the path of least
   * resistance. Moving it in here means the reviewer has the outline, the
   * lesson previews and the before/after compare in front of them when they
   * decide.
   */
  const listingReview = useMutation({
    mutationFn: (args: {
      decision: 'approved' | 'rejected';
      reason?: string;
      alsoContent?: boolean;
    }) => reviewCreatorCourse(id, args.decision, args.reason, { alsoContent: args.alsoContent }),
    meta: { successMessage: 'Listing decision saved.' },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-all-courses'] });
      void qc.invalidateQueries({ queryKey: ['admin-pending-courses'] });
      void qc.invalidateQueries({ queryKey: ['admin-content-pending'] });
      void qc.invalidateQueries({ queryKey: ['admin-course', id] });
      void qc.invalidateQueries({ queryKey: ['admin-course-content-changes', id] });
      onClose();
    },
  });

  // The API orders by `resolved_at ASC`, and postgres sorts NULLs LAST — so
  // the rows that still need a decision arrive at the BOTTOM. Regroup here
  // rather than trust the order.
  const openChanges = changes.filter((c) => !c.resolvedAt);
  const settledChanges = changes.filter((c) => c.resolvedAt);
  const pending = data?.contentReviewStatus === 'pending';
  // A house course has no listing decision to make — the API refuses the call,
  // and offering the button would be a promise the server breaks.
  const reviewable = Boolean(data?.creatorUserId) && data?.creatorIsHouse === false;
  const listingPending = reviewable && data?.productApprovalStatus === 'pending';
  /**
   * First submission: nobody has ever ruled on this course. There is no
   * "what changed" to show, so the dialog says so and points the reviewer at
   * the outline below instead of an empty diff.
   */
  const firstSubmission = reviewable && !data?.contentApprovedAt;

  return (
    <>
      <Dialog open onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{data?.productName ?? 'Course'}</DialogTitle>
          </DialogHeader>
          {data && (
            <div className="max-h-[65vh] space-y-4 overflow-auto text-sm">
              <div className="flex flex-wrap items-center gap-2 text-muted-foreground">
                <span>
                  {data.enrollmentCount} enrolled ·{' '}
                  <span className="capitalize">{data.accessLevel}</span> ·{' '}
                  <span className="capitalize">{data.status}</span>
                </span>
                {data.contentReviewStatus === 'pending' && (
                  <Badge variant="warning">Content pending</Badge>
                )}
                {data.contentReviewStatus === 'rejected' && (
                  <Badge variant="muted">Content rejected</Badge>
                )}
                {listingPending && <Badge variant="warning">Listing pending</Badge>}
                {reviewable && data.productApprovalStatus === 'rejected' && (
                  <Badge variant="muted">Listing rejected</Badge>
                )}
                {firstSubmission && <Badge variant="outline">New course</Badge>}
              </div>
              {firstSubmission && (
                <p className="rounded border border-dashed p-2 text-xs text-muted-foreground">
                  Nobody has reviewed this course yet, so there is nothing to compare it against.
                  Open a few lessons below before deciding.
                </p>
              )}

              {preview && (
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{preview.title}</span>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="ml-auto"
                      onClick={() => setPreview(null)}
                    >
                      Close preview
                    </Button>
                  </div>
                  <LessonPreviewPane preview={preview} />
                </div>
              )}

              {changes.length > 0 && (
                <div className="rounded border p-3">
                  <div className="mb-1 font-semibold">
                    Changes since last approval
                    {openChanges.length > 0 ? ` (${openChanges.length} open)` : ''}
                  </div>
                  <ul className="divide-y">
                    {[...openChanges, ...settledChanges].map((c) => (
                      <ContentChangeRow key={c.id} change={c} />
                    ))}
                  </ul>
                </div>
              )}

              {data.promo && (
                <div className="flex items-center gap-2 rounded border bg-muted/40 p-2">
                  <span className="text-sm font-semibold">Promo video</span>
                  <span className="text-xs text-muted-foreground">
                    ·{' '}
                    {data.promo.sourceType === 'r2' ? `upload (${data.promo.videoStatus})` : 'link'}
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    className="ml-auto"
                    disabled={openPromo.isPending}
                    onClick={() => openPromo.mutate()}
                  >
                    Open
                  </Button>
                </div>
              )}
              {data.chapters.map((ch) => (
                <div key={ch.id}>
                  <div className="font-semibold">{ch.title}</div>
                  <ul className="ml-1 divide-y">
                    {ch.lessons.map((l) => (
                      <li key={l.id} className="flex items-center gap-2 py-1.5">
                        <span>{l.title}</span>
                        <span className="text-xs text-muted-foreground">
                          ·{' '}
                          {l.sourceType === 'r2'
                            ? `upload (${l.videoStatus})`
                            : l.sourceType === 'article'
                              ? 'article'
                              : 'embed'}
                          {l.isPreview ? ' · preview' : ''}
                        </span>
                        {/* Under `hold` the source named above is the version
                            awaiting THIS decision — Open plays it, students are
                            still on the approved one. Saying so is the
                            difference between reviewing a change and reviewing
                            what is already live. */}
                        {l.heldPending && <Badge variant="warning">Held for approval</Badge>}
                        <Button
                          size="sm"
                          variant="outline"
                          className="ml-auto"
                          disabled={openLesson.isPending}
                          onClick={() => openLesson.mutate(l.id)}
                        >
                          Open
                        </Button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
          {/* Content review is only offered while the course is actually
              pending — the API refuses the call otherwise, and an
              always-visible button would be a promise the server breaks. */}
          {(pending || listingPending) && (
            <DialogFooter className="flex-wrap gap-2">
              {/* Rejections keep their reason dialog — a rejection without a
                  reason is a creator left guessing. Approvals no longer have a
                  confirm step: the reviewer is looking at the diff they are
                  approving, and a modal that only repeats the button's own
                  label is a second click, not a second thought. */}
              {listingPending && (
                <Button
                  variant="ghost"
                  disabled={listingReview.isPending}
                  onClick={() => setListingRejectOpen(true)}
                >
                  Reject listing
                </Button>
              )}
              {pending && (
                <Button
                  variant="ghost"
                  disabled={contentReview.isPending}
                  onClick={() => setRejectOpen(true)}
                >
                  Reject content
                </Button>
              )}
              {/* ONE Approve when both states are open. A single editorial edit
                  opens both (the sellable reset queues the listing, the tier-3
                  row queues the content) and this dialog shows one diff for
                  both, so two Approve buttons asked the reviewer to rule twice
                  on one change. The server settles both in one transaction. */}
              {pending && listingPending ? (
                <Button
                  disabled={listingReview.isPending || contentReview.isPending}
                  onClick={() => listingReview.mutate({ decision: 'approved', alsoContent: true })}
                >
                  Approve
                </Button>
              ) : listingPending ? (
                <Button
                  disabled={listingReview.isPending}
                  onClick={() => listingReview.mutate({ decision: 'approved' })}
                >
                  Approve listing
                </Button>
              ) : (
                <Button
                  disabled={contentReview.isPending}
                  onClick={() => contentReview.mutate({ decision: 'approved' })}
                >
                  Approve content
                </Button>
              )}
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
      {/* The rejection dialogs are SIBLINGS of the peek, not children of its
          content: a dialog nested inside another dialog's content fights it for
          the focus trap. */}
      <ReviewDecisionDialog
        open={listingRejectOpen}
        onOpenChange={setListingRejectOpen}
        title="Reject this listing"
        description="The course stays off the storefront and the creator is told why. The edit itself is not undone — the creator changes it and resubmits."
        label="Reason (optional)"
        placeholder="What needs to change before this can sell…"
        confirmLabel="Reject listing"
        pending={listingReview.isPending}
        onConfirm={(note) =>
          listingReview.mutate({ decision: 'rejected', reason: note || undefined })
        }
      />
      <ReviewDecisionDialog
        open={rejectOpen}
        onOpenChange={setRejectOpen}
        title="Reject changed content"
        description="The creator is told the new content was refused. This is separate from the listing decision — an approved listing stays approved."
        label="Reason (optional)"
        placeholder="What is wrong with the new content…"
        confirmLabel="Reject content"
        pending={contentReview.isPending}
        onConfirm={(note) =>
          contentReview.mutate({ decision: 'rejected', reason: note || undefined })
        }
      />
    </>
  );
}

// ── Q&A ─────────────────────────────────────────────────────────────────────

function QuestionsTab() {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const { data } = useQuery({
    queryKey: ['admin-questions', page],
    queryFn: () => listAllQuestions({ page, pageSize }),
  });
  const rows = data?.rows ?? [];
  const hide = useMutation({
    mutationFn: ({ id, hidden }: { id: string; hidden: boolean }) => setQuestionHidden(id, hidden),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-questions'] }),
  });
  return (
    <Card>
      <CardContent className="p-0">
        <table className="w-full text-sm">
          <thead className="border-b text-left text-muted-foreground">
            <tr>
              <th className="p-3">When</th>
              <th className="p-3">Author</th>
              <th className="p-3">Lesson</th>
              <th className="p-3">Text</th>
              <th className="p-3" />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className={`border-b last:border-0 ${r.hidden ? 'opacity-60' : ''}`}>
                <td className="p-3 text-xs text-muted-foreground">
                  {new Date(r.createdAt).toLocaleString()}
                </td>
                <td className="p-3">
                  {r.authorName ?? '—'}
                  {r.isCreatorReply && (
                    <Badge className="ml-1" variant="outline">
                      Instructor
                    </Badge>
                  )}
                  {r.parentId && (
                    <span className="ml-1 text-xs text-muted-foreground">(reply)</span>
                  )}
                </td>
                <td className="p-3">{r.lessonTitle}</td>
                <td className="max-w-md p-3">
                  <div className="line-clamp-2">{r.body}</div>
                </td>
                <td className="p-3 text-right">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => hide.mutate({ id: r.id, hidden: !r.hidden })}
                  >
                    {r.hidden ? 'Unhide' : 'Hide'}
                  </Button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={5} className="p-8 text-center text-muted-foreground">
                  No questions yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {(data?.total ?? 0) > pageSize && (
          <div className="flex items-center justify-between border-t p-3 text-sm">
            <span className="text-muted-foreground">
              Page {page} · {data?.total ?? 0} questions
            </span>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Previous
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={page * pageSize >= (data?.total ?? 0)}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Payouts ─────────────────────────────────────────────────────────────────

function PayoutsTab() {
  const qc = useQueryClient();
  const [status, setStatus] = useState('requested');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const { data, isError, isLoading } = useQuery({
    queryKey: ['admin-creator-payouts', status],
    queryFn: () => listCreatorPayouts({ status }),
  });
  const { data: batches } = useQuery({
    queryKey: ['admin-creator-payout-batches'],
    queryFn: () => listCreatorPayoutBatches(),
  });
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['admin-creator-payouts'] });
    qc.invalidateQueries({ queryKey: ['admin-creator-payout-batches'] });
  };
  const toggle = (id: string) =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const createBatch = async () => {
    if (selected.size === 0) return;
    const now = new Date();
    await createCreatorPayoutBatch({
      periodStart: new Date(now.getFullYear(), now.getMonth(), 1).toISOString(),
      periodEnd: new Date(now.getFullYear(), now.getMonth() + 1, 1).toISOString(),
      requestIds: [...selected],
    });
    setSelected(new Set());
    invalidate();
  };
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <StatusFilter
          value={status}
          onChange={setStatus}
          options={['requested', 'processing', 'paid', 'rejected', 'cancelled']}
        />
        {status === 'requested' && (
          <Button size="sm" disabled={selected.size === 0} onClick={createBatch}>
            Batch {selected.size || ''} request{selected.size === 1 ? '' : 's'}
          </Button>
        )}
      </div>
      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="border-b text-left text-muted-foreground">
              <tr>
                <th className="w-8 p-3" />
                <th className="p-3">Creator</th>
                <th className="p-3">Gross</th>
                <th className="p-3">Net</th>
                <th className="p-3">Method</th>
                <th className="p-3">Status</th>
                <th className="p-3" />
              </tr>
            </thead>
            <tbody>
              {(data?.rows ?? []).map((r) => (
                <tr key={r.id} className="border-b last:border-0">
                  <td className="p-3">
                    {r.status === 'requested' && (
                      <input
                        type="checkbox"
                        checked={selected.has(r.id)}
                        onChange={() => toggle(r.id)}
                      />
                    )}
                  </td>
                  <td className="p-3">
                    {r.displayName}
                    <div className="text-xs text-muted-foreground">{r.ownerEmail}</div>
                  </td>
                  <td className="p-3 tabular-nums">₱{Number(r.amount).toFixed(2)}</td>
                  <td className="p-3 tabular-nums">₱{Number(r.netAmount).toFixed(2)}</td>
                  <td className="p-3">
                    {r.method}
                    <div className="text-xs text-muted-foreground">
                      {String((r.payload as { account?: string })?.account ?? '')}
                    </div>
                  </td>
                  <td className="p-3">
                    <Badge variant={statusVariant[r.status]} className="capitalize">
                      {r.status}
                    </Badge>
                  </td>
                  <td className="p-3 text-right">
                    {r.status === 'requested' && (
                      <div className="flex justify-end gap-1">
                        <Button
                          size="sm"
                          onClick={() => markCreatorPayoutPaid(r.id).then(invalidate)}
                        >
                          Mark paid
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => rejectCreatorPayout(r.id).then(invalidate)}
                        >
                          Reject
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {isLoading && (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-muted-foreground">
                    Loading payout requests…
                  </td>
                </tr>
              )}
              {isError && (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-destructive">
                    Could not load payout requests. Refresh to try again.
                  </td>
                </tr>
              )}
              {data && data.rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-muted-foreground">
                    No {status} payout requests.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <h2 className="pt-2 text-sm font-semibold">Batches</h2>
      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="border-b text-left text-muted-foreground">
              <tr>
                <th className="p-3">Period</th>
                <th className="p-3">Total</th>
                <th className="p-3">Status</th>
                <th className="p-3" />
              </tr>
            </thead>
            <tbody>
              {(batches ?? []).map((b) => (
                <tr key={b.id} className="border-b last:border-0">
                  <td className="p-3">
                    {new Date(b.periodStart).toLocaleDateString()} –{' '}
                    {new Date(b.periodEnd).toLocaleDateString()}
                  </td>
                  <td className="p-3 tabular-nums">₱{Number(b.total).toFixed(2)}</td>
                  <td className="p-3">
                    <Badge variant={statusVariant[b.status]} className="capitalize">
                      {b.status}
                    </Badge>
                  </td>
                  <td className="p-3 text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => exportCreatorPayoutBatch(b.id)}
                      >
                        Export CSV
                      </Button>
                      {b.status === 'processing' && (
                        <>
                          <Button
                            size="sm"
                            onClick={() => markCreatorPayoutBatchPaid(b.id).then(invalidate)}
                          >
                            Mark paid
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => rejectCreatorPayoutBatch(b.id).then(invalidate)}
                          >
                            Reject
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {batches && batches.length === 0 && (
                <tr>
                  <td colSpan={4} className="p-8 text-center text-muted-foreground">
                    No batches yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

// ── helpers ─────────────────────────────────────────────────────────────────

function StatusFilter({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: string[];
}) {
  return (
    <div className="flex gap-1">
      {options.map((o) => (
        <Button
          key={o}
          size="sm"
          variant={value === o ? 'default' : 'outline'}
          className="capitalize"
          onClick={() => onChange(o)}
        >
          {o}
        </Button>
      ))}
    </div>
  );
}

function Labeled({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <label className="text-xs font-medium text-muted-foreground">{label}</label>
      {children}
    </div>
  );
}
